import { SuperDispatchLaneRecord } from 'lib/api/getQuickQuote';
import {
  AutoVehicleType,
  autoVehicleTypeLabel,
  mapSuperDispatchVehicleType,
} from 'lib/autoTransport/autoVehicleTypes';
import { formatAutoVehicleLabel } from 'lib/autoTransport/formatAutoVehicleLabel';
import { Undef } from 'types/UtilityTypes';
import { titleCase } from 'utils/formatStrings';

type LaneVehicle = SuperDispatchLaneRecord['vehicles'][number];

// Turns "2026-03-15" or "2026-03-15T14:00:00Z" into "Mar 15". Uses UTC so a
// date-only value doesn't show as the day before in US timezones.
export function formatLaneDate(value?: string): string {
  if (!value) {
    return '';
  }

  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) {
    return '';
  }

  const date = new Date(
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  );

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

// "2020 Toyota Camry (sedan)" or "2020 Toyota Camry (sedan) (inoperable)".
// Uses the vehicle type, then "Vehicle", when year, make, and model are all
// missing. Type suffix is omitted when the label is already just the type.
export function formatLaneVehicle(vehicle: Undef<LaneVehicle>): string {
  if (!vehicle) {
    return 'Vehicle';
  }

  const name = formatLaneVehicleDisplayName(vehicle);
  const typeLabel = vehicle.type ? autoVehicleTypeLabel(vehicle.type) : '';
  const typeSuffix = vehicle.type
    ? formatLaneVehicleTypeSuffix(vehicle.type)
    : '';
  const baseLabel = name || typeLabel || 'Vehicle';
  const label = name && typeSuffix ? `${baseLabel} (${typeSuffix})` : baseLabel;

  return vehicle.isInoperable ? `${label} (inoperable)` : label;
}

// Compact body-type labels for the sidebar: "(sedan)", "(SUV)".
function formatLaneVehicleTypeSuffix(vehicleType: string): string {
  if (vehicleType.trim().toLowerCase() === 'pickup') {
    return 'pickup';
  }

  const mapped = mapSuperDispatchVehicleType(vehicleType);
  switch (mapped) {
    case AutoVehicleType.Sedan:
      return 'sedan';
    case AutoVehicleType.Coupe2Doors:
      return 'coupe';
    case AutoVehicleType.Suv:
      return 'SUV';
    case AutoVehicleType.Pickup2Doors:
    case AutoVehicleType.Pickup4Doors:
      return 'pickup';
    case AutoVehicleType.Van:
      return 'van';
    default:
      return '';
  }
}

function formatLaneVehicleDisplayName(vehicle: LaneVehicle): string {
  const year =
    vehicle.year != null && String(vehicle.year).trim() !== ''
      ? String(vehicle.year).trim()
      : '';
  const make = vehicle.make?.trim() ? titleCase(vehicle.make.trim()) : '';
  const model = vehicle.model?.trim()
    ? formatLaneModelDisplayName(vehicle.model.trim())
    : '';

  if (year || make || model) {
    return [year, make, model].filter(Boolean).join(' ');
  }

  return formatAutoVehicleLabel(vehicle);
}

// Title-case model names but keep short trim codes (IS, RS, ILX) uppercase.
function formatLaneModelDisplayName(model: string): string {
  return model.split(/\s+/).filter(Boolean).map(formatLaneModelToken).join(' ');
}

const LANE_MODEL_LOWERCASE_WORDS = new Set(['air', 'max', 'one', 'pro', 'van']);

function formatLaneModelToken(token: string): string {
  if (/^\d+$/.test(token)) {
    return token;
  }

  if (token.includes('-')) {
    return token
      .split('-')
      .map((part) => {
        if (part.length === 1) {
          return part.toLowerCase();
        }
        return formatLaneModelToken(part);
      })
      .join('\u2011');
  }

  if (/^[a-z]{1,4}\d+$/i.test(token)) {
    return token.toUpperCase();
  }

  if (/^[a-z]\d+[a-z]*$/i.test(token)) {
    return token.charAt(0).toUpperCase() + token.slice(1).toLowerCase();
  }

  const lower = token.toLowerCase();
  if (/^[a-z]{2,3}$/.test(lower) && !LANE_MODEL_LOWERCASE_WORDS.has(lower)) {
    return lower.toUpperCase();
  }

  return titleCase(token);
}

// "West Palm Beach, FL to Buford, GA". Leaves out whichever end is missing.
export function formatLaneRoute(record: SuperDispatchLaneRecord): string {
  const pickup = joinCityState(record.pickupCity, record.pickupState);
  const delivery = joinCityState(record.deliveryCity, record.deliveryState);

  return [pickup, delivery].filter(Boolean).join(' to ');
}

// Posted loads: Super Dispatch labels these as posted/available, but the
// available date can precede posted and can be confusing. Show posted only.
export function formatPostingMeta(record: SuperDispatchLaneRecord): string {
  return formatLaneDate(record.postingDate);
}

// "Sep 29 -> Oct 6" when both dates exist; otherwise the single formatted date.
export function formatMoveMeta(record: SuperDispatchLaneRecord): string {
  return formatCompactDateRange(record.dispatchedDate, record.deliveredDate);
}

// Dates plus a compact vehicle count when collapsed rows hide extra names.
export function formatLaneRecordMetaLine(
  meta: string,
  vehicleCount: number,
  expanded: boolean
): string {
  if (vehicleCount > 2 && !expanded) {
    const countLabel = `${vehicleCount} vehicles`;
    if (meta) {
      return `${meta} · ${countLabel}`;
    }
    return countLabel;
  }

  return meta;
}

function joinCityState(city?: string, state?: string): string {
  return [city, state].filter(Boolean).join(', ');
}

function formatCompactDateRange(first?: string, second?: string): string {
  const start = first ? formatLaneDate(first) : '';
  const end = second ? formatLaneDate(second) : '';

  if (start && end) {
    return `${start} › ${end}`;
  }

  if (start) {
    return start;
  }

  if (end) {
    return end;
  }

  return '';
}
